import { GENERAL_CONSTANTS } from 'constants/general';
import React, { useState, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';
import styled from 'styled-components';

const TooltipContainer = styled.div`
  position: absolute;
  background-color: rgba(0, 0, 0, 0.8);
  color: white;
  padding: 5px 10px;
  border-radius: 4px;
  font-size: 12px;
  z-index: 10000;
  pointer-events: none;
  white-space: nowrap;
  text-align: center;
`;

const ReadOnlyContainer = styled.div<{ isReadOnly: boolean }>`
  ${({ isReadOnly }) =>
    isReadOnly &&
    `
    opacity: 0.4;
    cursor: not-allowed;
    height: fit-content;

    button, 
    input, 
    select, 
    label,
    #selection-list-checkbox,
    textarea {
      pointer-events: none;
    }
  `}
`;

interface ReadOnlyWrapperProps {
  readOnly?: boolean;
  children: React.ReactNode;
  className?: string;
  tooltipMessage?: string
  hideTooltip?: boolean;
}

export const ReadOnlyWrapper: React.FC<ReadOnlyWrapperProps> = ({
  readOnly = false,
  children,
  className,
  tooltipMessage = GENERAL_CONSTANTS.FEATURE_DISABLE_FOR_ROLE,
  hideTooltip = false
}) => {
  const [showTooltip, setShowTooltip] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const [tooltipRef, setTooltipRef] = useState<HTMLDivElement | null>(null);
  const ref = useRef<HTMLDivElement>(null);

  // Function to update tooltip position based on mouse coordinates
  const handleMouseMove = (e: MouseEvent) => {
    if (showTooltip && tooltipRef) {
      const tooltipRect = tooltipRef.getBoundingClientRect();
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      
      // Position tooltip near cursor with a small offset
      let left = e.clientX + 10;
      let top = e.clientY + 10;
      
      // Adjust if tooltip would go beyond right edge
      if (left + tooltipRect.width > viewportWidth) {
        left = e.clientX - tooltipRect.width - 10;
      }
      
      // Adjust if tooltip would go beyond bottom edge
      if (top + tooltipRect.height > viewportHeight) {
        top = e.clientY - tooltipRect.height - 10;
      }
      
      setPosition({
        left: left + window.scrollX,
        top: top + window.scrollY
      });
    }
  };

  useEffect(() => {
    // Add mousemove event listener when tooltip is shown
    if (showTooltip) {
      window.addEventListener('mousemove', handleMouseMove);
    }
    
    // Clean up event listener
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
    };
  }, [showTooltip, tooltipRef]);

  return (
    <>
      <ReadOnlyContainer
        ref={ref}
        className={className}
        isReadOnly={readOnly}
        onMouseEnter={() => readOnly && setShowTooltip(true)}
        onMouseLeave={() => readOnly && setShowTooltip(false)}
      >
        {children}
      </ReadOnlyContainer>
      {showTooltip && readOnly && !hideTooltip && ReactDOM.createPortal(
        <TooltipContainer
          ref={setTooltipRef}
          style={{
            top: `${position.top}px`,
            left: `${position.left}px`,
            // Remove transform to position based on cursor precisely
          }}
        >
          {tooltipMessage}
        </TooltipContainer>,
        document.body
      )}
    </>
  );
};